<template>
  <div class="ImagePicker" v-bind="$attrs">
    <div class="ImagePicker-image d-flex flex-column position-relative">
      <ImagePickerDuplicatedCover
        v-if="isDuplicated && !isEditing"
        :image="getUrlImageSelected"
      />
      <template v-else>
        <div class="d-flex" v-if="Array.isArray(getUrlImageSelected)">
          <BaseImage
            class="mr-2"
            v-for="(image, index) in getUrlImageSelected"
            :key="index"
            :image="image.url_image"
          />
        </div>
        <div v-else>
          <BaseImage :image="getUrlImageSelected" />
        </div>
      </template>
      <button
        v-if="isEditing"
        :disabled="disableButton"
        class="btn btn-block ImagePicker-button"
        :class="{ disabled: disableButton }"
        @click="onToggleModal"
      >
        <svg class="svg-icon ImagePicker-icon" viewBox="0 0 20 20">
          <path
            d="M10,6.536c-2.263,0-4.099,1.836-4.099,4.098S7.737,14.732,10,14.732s4.099-1.836,4.099-4.098S12.263,6.536,10,6.536M10,13.871c-1.784,0-3.235-1.453-3.235-3.237S8.216,7.399,10,7.399c1.784,0,3.235,1.452,3.235,3.235S11.784,13.871,10,13.871M17.118,5.672l-3.237,0.014L12.52,3.697c-0.082-0.105-0.209-0.168-0.343-0.168H7.824c-0.134,0-0.261,0.062-0.343,0.168L6.12,5.686H2.882c-0.951,0-1.726,0.748-1.726,1.699v7.362c0,0.951,0.774,1.725,1.726,1.725h14.236c0.951,0,1.726-0.773,1.726-1.725V7.195C18.844,6.244,18.069,5.672,17.118,5.672 M17.98,14.746c0,0.477-0.386,0.861-0.862,0.861H2.882c-0.477,0-0.863-0.385-0.863-0.861V7.384c0-0.477,0.386-0.85,0.863-0.85l3.451,0.014c0.134,0,0.261-0.062,0.343-0.168l1.361-1.989h3.926l1.361,1.989c0.082,0.105,0.209,0.168,0.343,0.168l3.451-0.014c0.477,0,0.862,0.184,0.862,0.661V14.746z"
          ></path>
        </svg>
        <br />{{ labelButton }}
      </button>
    </div>
    <Portal to="imagePickerModalPortal" v-if="showModal">
      <ImagePickerModal @onClose="onToggleModal">
        <template v-slot:title>
          <h4 class="modal-title">{{ labelSearch }}</h4>
        </template>
        <template v-slot:body>
          <ImagePickerSearch
            :countries="countries"
            :isSearching="isSearching"
            :defaultParams="defaultParams"
            @onSearch="onSearch"
          />

          <ImagePickerImageGrid v-if="images.length > 0"
            :data="images"
            :countries="countries"
            :isSearching="isSearching"
            :limitMinOfSelectedImages="limitMinOfSelectedImages"
            :limitMaxOfSelectedImages="limitMaxOfSelectedImages"
            @onSelectImage="onSelectImage"
            @onClose="onToggleModal"
          />

          <p v-else style="text-align: center;" class="my-4">
            There are no images, please try again
          </p>

          <div class="mt-2" v-if="total >= 0">Search results: {{ total }}</div>
          <ImagePickerPagination
            :total="total"
            :perPage="defaultParams.limit"
            :currentPage="currentPage"
            :isSearching="isSearching"
            @onPage="onPage"
          />
        </template>
        <template v-slot:upload>
          <ImagePickerUpload
            :countries="countries"
            :isSearching="isSearching"
            :defaultParams="defaultParams"
            @onSelectCountry="onSelectCountry"
            @onSelectCity="onSelectCity"
            @onRemovingCountry="onRemovingCountry"
          />
          <ImageUploader
            :countries="countries"
            :data="images"
            :defaultParams="defaultParams"
            :isSearching="isSearching"
            :paramsSelected="paramsSelected"
          />
        </template>
      </ImagePickerModal>
    </Portal>
  </div>
</template>

<script>
import ImagePickerModal from './ImagePickerModal.vue'
import ImagePickerSearch from './ImagePickerSearch.vue'
import ImagePickerImageGrid from './ImagePickerImageGrid.vue'
import ImagePickerPagination from './ImagePickerPagination.vue'
import ImagePickerDuplicatedCover from './ImagePickerDuplicatedCover.vue'
import ImageUploader from './ImageUploader.vue'
import ImagePickerUpload from './ImagePickerUpload.vue'

export default {
  name: 'ImagePicker',
  inheritAttrs: false,
  components: {
    ImagePickerModal,
    ImagePickerSearch,
    ImagePickerImageGrid,
    ImagePickerPagination,
    ImagePickerDuplicatedCover,
    ImageUploader,
    ImagePickerUpload
  },
  data () {
    return {
      showModal: false,
      params: null,
      urlImageSelected: '',
      page: 0,
      paramsSelected: {
        country: this.defaultParams.country || '',
        city: this.defaultParams.city || ''
      }
    }
  },
  computed: {
    currentPage () {
      return this.page
    },
    getUrlImageSelected () {
      if (this.urlImageSelected) {
        return this.urlImageSelected
      }
      if (this.imageUrl) {
        return this.imageUrl
      }
      return ''
    }
  },
  props: {
    imageUrl: {
      type: [String, Array],
      default: ''
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    total: {
      type: Number,
      default: 0
    },
    images: {
      type: Array,
      required: true
    },
    countries: {
      type: [String, Array],
      required: true
    },
    labelSearch: {
      type: String,
      default: function () {
        return 'Select image'
      }
    },
    labelButton: {
      type: String,
      default: 'Change image'
    },
    isSearching: {
      type: Boolean,
      required: true
    },
    defaultParams: {
      type: Object,
      required: false
    },
    isDuplicated: {
      type: Boolean,
      default: false
    },
    limitMinOfSelectedImages: {
      type: Number,
      required: false
    },
    limitMaxOfSelectedImages: {
      type: Number,
      default: 1
    },
    disableButton: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    onToggleModal () {
      if (this.defaultParams && this.defaultParams.limit) {
        this.onSearch(this.defaultParams)
      }
      this.showModal = !this.showModal
      document.querySelector('#portletFoot').classList.toggle('sticky-bottom')
      document
        .querySelector('#portletFoot button')
        .classList.toggle('invisible')
    },
    onPage (page) {
      this.page = page
      this.params.offset = Number(this.params.limit) * Number(page)
      this.onSearch(this.params)
    },
    onSearch (params) {
      this.params = params
      this.$emit('onSearch', params)
    },
    onSelectImage (imageSelected) {
      if (imageSelected) {
        this.urlImageSelected = imageSelected.url
      }
      this.$emit('onSelectImage', imageSelected)
      if (this.limitMaxOfSelectedImages === 1) {
        this.onToggleModal()
      }
    },
    onSelectCountry (data) {
      this.paramsSelected['country'] = data
      this.paramsSelected['city'] = ''
    },
    onSelectCity (data) {
      this.paramsSelected['city'] = data
    },
    onRemovingCountry (data) {
      this.paramsSelected['country'] = ''
      this.paramsSelected['city'] = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.ImagePicker-button {
  position: absolute;
  bottom: 0;
  background: rgba(32, 111, 145, 0.68);
  background: -webkit-gradient(
    linear,
    left top,
    right top,
    from(rgba(32, 111, 145, 0.68)),
    color-stop(24%, rgba(45, 111, 143, 0.68)),
    color-stop(38%, rgba(91, 120, 160, 0.68)),
    color-stop(48%, rgba(114, 119, 146, 0.68)),
    color-stop(61%, rgba(127, 116, 137, 0.68)),
    color-stop(71%, rgba(157, 105, 110, 0.68)),
    color-stop(95%, rgba(193, 50, 62, 0.68))
  );
  background: linear-gradient(
    90deg,
    rgba(32, 111, 145, 0.68) 0%,
    rgba(45, 111, 143, 0.68) 24%,
    rgba(91, 120, 160, 0.68) 38%,
    rgba(114, 119, 146, 0.68) 48%,
    rgba(127, 116, 137, 0.68) 61%,
    rgba(157, 105, 110, 0.68) 71%,
    rgba(193, 50, 62, 0.68) 95%
  );
  padding: 6px 0;
  color: #ffffff;
  border-radius: 0;
  font-size: 0.9rem;
  -webkit-transition: 0.7s; /* For Safari 3.1 to 6.0 */
  transition: 0.7s;
  width: 100%;
  max-width: 305px;
}
.ImagePicker-button:hover {
  padding: 66px 0;
}
.test {
  z-index: 100000;
  background: black;
}
</style>
